import { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MoreHorizontal, X } from "lucide-react";
import type { RoutePath } from "@wedding/shared";
import { cn } from "./cn";

export type MobileNavItem = {
  /**
   * Marka tipli hedef — yalnız tüketen app'in `paths.ts`'inden gelebilir.
   * Bu dosya ham `NavLink` içe aktaran iki sanctioned "sink"ten biri
   * (diğeri: her app'in `src/lib/route-primitives.tsx`'i); `check:routes`'un
   * allowlist'inde. `RoutePath extends string` olduğu için aşağıdaki
   * `to={item.to}` ve `startsWith` kullanımları aynen çalışır.
   */
  to: RoutePath;
  icon: React.ComponentType<{ className?: string }>;
  /** Already-translated label (the consuming app calls t()). */
  label: string;
  end?: boolean;
  /** Optional count badge (e.g. moderation pending). */
  badge?: number;
};

/** Repo-wide focus-visible treatment (matches ThemeToggle in organizer/web). */
const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2";

function CountBadge({ count, className }: { count: number; className?: string }) {
  if (count <= 0) return null;
  return (
    <span
      className={cn(
        "min-w-4 h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold flex items-center justify-center tabular-nums leading-none",
        className,
      )}
    >
      {count > 99 ? "99+" : count}
    </span>
  );
}

/**
 * Mobile bottom tab bar with overflow: the first `maxVisible` items stay in the
 * bar, the rest move into a "More" bottom sheet. This is the market-standard
 * iOS/Android pattern — a horizontally scrolling bar hides items with no
 * affordance and can push the active tab off-screen.
 *
 * Presentation only: it never fetches. Badge counts are computed by the app and
 * passed in via `navItems`. A badge on an OVERFLOW item rolls up to an
 * attention dot on the "More" button, so nothing hidden goes unnoticed.
 */
export function MobileBottomNav({
  navItems,
  maxVisible = 4,
}: {
  navItems: MobileNavItem[];
  maxVisible?: number;
}) {
  const { t } = useTranslation();
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const moreButtonRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  const visible = navItems.slice(0, maxVisible);
  const overflow = navItems.slice(maxVisible);
  const hasOverflow = overflow.length > 0;

  const isItemActive = (item: MobileNavItem) =>
    item.end ? location.pathname === item.to : location.pathname.startsWith(item.to);
  const overflowActive = hasOverflow && overflow.some(isItemActive);
  const overflowBadge = overflow.reduce((sum, it) => sum + (it.badge ?? 0), 0);

  // Dismissing the sheet returns focus to the button that opened it, so keyboard
  // and screen-reader users don't get dropped at the top of the document.
  const closeSheet = useCallback(() => {
    setSheetOpen(false);
    moreButtonRef.current?.focus();
  }, []);

  // Navigating closes the sheet — no focus restore here, the destination owns focus.
  useEffect(() => {
    setSheetOpen(false);
  }, [location.pathname]);

  // Move focus into the sheet on open (it's role="dialog").
  useEffect(() => {
    if (sheetOpen) sheetRef.current?.focus();
  }, [sheetOpen]);

  useEffect(() => {
    if (!sheetOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeSheet();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [sheetOpen, closeSheet]);

  return (
    <>
      <nav className="glass fixed bottom-0 inset-x-0 z-50 border-t border-primary/10 md:hidden safe-bottom touch-manipulation">
        <div className="flex items-center justify-around px-2 pt-2 pb-5">
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  "flex flex-col items-center gap-1 py-1 px-2 transition-colors min-w-0 flex-1 rounded-lg",
                  FOCUS_RING,
                  isActive ? "text-primary" : "text-muted-foreground hover:text-foreground",
                )
              }
            >
              <span className="relative">
                <item.icon className="h-5 w-5 shrink-0" />
                {item.badge ? (
                  <CountBadge count={item.badge} className="absolute -top-1.5 -right-2.5 scale-90" />
                ) : null}
              </span>
              <span className="flex h-6 w-full items-center justify-center text-center">
                <span className="text-[10px] font-bold uppercase tracking-tighter leading-[1.1] line-clamp-2">
                  {item.label}
                </span>
              </span>
            </NavLink>
          ))}

          {hasOverflow && (
            <button
              ref={moreButtonRef}
              type="button"
              onClick={() => setSheetOpen((v) => !v)}
              aria-label={t("common.more")}
              aria-expanded={sheetOpen}
              className={cn(
                "flex flex-col items-center gap-1 py-1 px-2 transition-colors min-w-0 flex-1 cursor-pointer rounded-lg",
                FOCUS_RING,
                overflowActive || sheetOpen ? "text-primary" : "text-muted-foreground hover:text-foreground",
              )}
            >
              <span className="relative">
                <MoreHorizontal className="h-5 w-5 shrink-0" />
                {overflowBadge > 0 && (
                  <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />
                )}
              </span>
              <span className="flex h-6 w-full items-center justify-center text-center">
                <span className="text-[10px] font-bold uppercase tracking-tighter leading-[1.1] line-clamp-2">
                  {t("common.more")}
                </span>
              </span>
            </button>
          )}
        </div>
      </nav>

      {hasOverflow && sheetOpen && (
        <>
          <div
            className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm md:hidden"
            onClick={closeSheet}
            aria-hidden="true"
          />
          <div
            ref={sheetRef}
            role="dialog"
            aria-modal="true"
            aria-label={t("common.otherPages")}
            tabIndex={-1}
            className="fixed bottom-0 inset-x-0 z-[70] md:hidden rounded-t-2xl border-t border-border bg-card shadow-2xl safe-bottom overscroll-contain focus:outline-none"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-border">
              <span className="text-sm font-semibold text-foreground">{t("common.otherPages")}</span>
              <button
                type="button"
                onClick={closeSheet}
                aria-label={t("common.close")}
                className={cn(
                  "p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer",
                  FOCUS_RING,
                )}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-4 gap-1 p-4">
              {overflow.map((item) => {
                const isActive = isItemActive(item);
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={cn(
                      "flex flex-col items-center gap-2 py-4 px-2 rounded-2xl transition-colors",
                      FOCUS_RING,
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-muted-foreground hover:bg-muted hover:text-foreground",
                    )}
                  >
                    <span className="relative">
                      <item.icon className="h-5 w-5" />
                      {item.badge ? (
                        <CountBadge count={item.badge} className="absolute -top-1.5 -right-2.5 scale-90" />
                      ) : null}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-tighter text-center leading-tight">
                      {item.label}
                    </span>
                  </NavLink>
                );
              })}
            </div>
          </div>
        </>
      )}
    </>
  );
}
